/**
 * Auth Session Guard
 * Prevents multiple OAuth sessions from starting simultaneously
 * Singleton pattern ensures only one auth session is active at a time
 */

class AuthSessionGuard {
	private static instance: AuthSessionGuard;
	private activeSession: string | null = null;
	private sessionPromise: Promise<any> | null = null;
	/** Bumped on clear/start so a stale in-flight session cannot wipe a newer one. */
	private sessionGeneration = 0;

	private constructor() {
		// Private constructor for singleton
	}

	public static getInstance(): AuthSessionGuard {
		if (!AuthSessionGuard.instance) {
			AuthSessionGuard.instance = new AuthSessionGuard();
		}
		return AuthSessionGuard.instance;
	}

	/**
	 * Check if an auth session is currently active
	 */
	public isSessionActive(): boolean {
		return this.activeSession !== null;
	}

	/**
	 * Get the currently active session type
	 */
	public getActiveSession(): string | null {
		return this.activeSession;
	}

	/**
	 * Start a new auth session
	 * Returns false if a session is already active
	 */
	public async startSession(
		sessionType: string,
		promptFn: () => Promise<any>
	): Promise<any> {
		// Check if another session is active
		if (this.activeSession && this.activeSession !== sessionType) {
			console.warn(
				`[AuthSessionGuard] Cannot start ${sessionType} - ${this.activeSession} is already active`
			);
			return {
				type: 'error',
				error: `Another authentication session (${this.activeSession}) is already in progress. Please wait.`,
			};
		}

		// If same session type is clicked again while active, return the existing promise
		if (this.activeSession === sessionType && this.sessionPromise) {
			console.log(
				`[AuthSessionGuard] ${sessionType} already active, returning existing promise`
			);
			return this.sessionPromise;
		}

		const generation = ++this.sessionGeneration;

		try {
			console.log(`[AuthSessionGuard] Starting ${sessionType} session`);
			this.activeSession = sessionType;

			// Store the promise so concurrent calls can wait for it
			this.sessionPromise = promptFn();
			const result = await this.sessionPromise;

			console.log(`[AuthSessionGuard] ${sessionType} session completed`);
			return result;
		} catch (error: any) {
			console.error(
				`[AuthSessionGuard] ${sessionType} session error:`,
				error
			);
			
			// Handle QuotaExceededError which prevents auth sessions from starting on web
			if (error?.name === 'QuotaExceededError' || error?.message?.includes('Quota') || error?.message?.includes('exceeded the quota')) {
				console.warn('[AuthSessionGuard] QuotaExceededError detected. Clearing localStorage and retrying...');
				if (typeof window !== 'undefined' && window.localStorage) {
					try {
						// Clear everything to ensure we have space for the auth session
						window.localStorage.clear();
						
						// Retry once after clearing
						this.sessionPromise = promptFn();
						const retryResult = await this.sessionPromise;
						console.log(`[AuthSessionGuard] ${sessionType} session completed after retry`);
						return retryResult;
					} catch (retryError) {
						console.error(`[AuthSessionGuard] ${sessionType} session retry error:`, retryError);
						throw retryError;
					}
				}
			}
			
			throw error;
		} finally {
			// Only clear if this call still owns the guard (logout may have superseded it)
			if (this.sessionGeneration === generation) {
				this.activeSession = null;
				this.sessionPromise = null;
			}
		}
	}

	/**
	 * Clear the active session (for cleanup/cancel / logout)
	 */
	public clearSession(): void {
		console.log(
			`[AuthSessionGuard] Clearing session: ${this.activeSession}`
		);
		this.sessionGeneration += 1;
		this.activeSession = null;
		this.sessionPromise = null;
	}

	/**
	 * Reset the guard (for testing)
	 */
	public static reset(): void {
		if (AuthSessionGuard.instance) {
			AuthSessionGuard.instance.clearSession();
		}
	}
}

// Export singleton instance getter
export const getAuthSessionGuard = () => AuthSessionGuard.getInstance();

export default AuthSessionGuard;
